import {useEffect,useRef,useState} from 'react';
import {useQuery,useQueryClient} from '@tanstack/react-query';
import {useNavigate,useSearchParams} from 'react-router-dom';
import {api} from '../api/client';
import {learning} from '../api/learning';
import {SectionPlayer} from './section-player';
import {savedLearningPosition} from './learning-position';

/** All lesson session entry paths resolve the published revision before rendering. */
export function LearningEntry({lessonId,sessionId}:{lessonId:string;sessionId:string}){
 const [search]=useSearchParams(),nav=useNavigate(),qc=useQueryClient();
 const content=useQuery({queryKey:['content',lessonId],queryFn:()=>api.content(lessonId),staleTime:0});
 const session=useQuery({queryKey:['learning-session',sessionId],queryFn:()=>learning.get(sessionId),staleTime:0});
 const [error,setError]=useState(false),redirect=useRef(''),entered=useRef('');
 useEffect(()=>{
  const routeKey=`${lessonId}:${sessionId}:${search.get('sectionId')??''}`;
  if(!content.data||!session.data||content.isFetching||session.isFetching||redirect.current===routeKey)return;
  const sections=content.data.sections.filter((s:any)=>s.type!=='reference');
  const current=content.data.lessonRevisionId===session.data.lessonRevisionId;
  const saved=savedLearningPosition(sessionId),requested=search.get('sectionId')??saved?.sectionId??session.data.currentPosition?.sectionId;
  if(current&&sections.some((s:any)=>s.id===requested)&&search.has('sectionId')){
   if(entered.current!==sessionId){entered.current=sessionId;void learning.create(lessonId,true).then(async next=>{
    await qc.invalidateQueries({queryKey:['current-course']});
    if(next.id!==sessionId)nav(`/lesson/${lessonId}/session/${next.id}?${search}`,{replace:true});
   }).catch(()=>{entered.current='';setError(true);});}
   return;
  }
  redirect.current=routeKey;
  void (async()=>{
   let next=session.data!,target=sections.find((s:any)=>s.id===requested);
   if(!current){
    // Exact title + kind only; ambiguous/removed sections fall back to the current overview.
    const old=requested?await learning.section(sessionId,requested):null;
    const matches=old?sections.filter((s:any)=>s.title===old.title&&s.type===old.type):[];
    if(matches.length!==1){nav(`/lesson/${lessonId}`,{replace:true});return;}
    target=matches[0];next=await learning.create(lessonId,true);
    await qc.invalidateQueries({queryKey:['current-course']});
   }
   target??=sections[0];if(!target)throw Error('No available section');
   const same=current&&saved?.sectionId===target.id;
   const params=new URLSearchParams({sectionId:target.id,origin:target.type==='foundation_practice'?'practice':'learning'});
   if(same){params.set('item',String(saved.item??0));params.set('page',String(saved.page??1));}
   nav(`/lesson/${lessonId}/session/${next.id}?${params}`,{replace:true});
  })().catch(()=>{redirect.current='';setError(true);});
 },[content.data,content.isFetching,session.data,session.isFetching,sessionId,lessonId,search,nav,qc]);
 if(error||content.isError||session.isError)return <div role="alert">Не удалось открыть актуальный раздел. <button onClick={()=>{setError(false);void content.refetch();void session.refetch();}}>Повторить</button></div>;
 if(content.isFetching||session.isFetching||!content.data||content.data.lessonRevisionId!==session.data?.lessonRevisionId||!content.data.sections.some((s:any)=>s.id===search.get('sectionId')))return <p role="status">Открываем раздел…</p>;
 return <SectionPlayer key={`${sessionId}:${search.get('sectionId')}`} lessonId={lessonId} sessionId={sessionId} sectionId={search.get('sectionId')}/>;
}
